import { useCallback, useEffect, useState } from 'react'
import { ArrowRightLeft, Loader2, Check, X } from 'lucide-react'
import { listIncomingTransfers, acceptTransfer, declineTransfer, type AppTransfer } from '../lib/api'

interface IncomingTransfersBannerProps {
  /** Called after a transfer is accepted (ownership moved) so the parent can refresh its app list. */
  onAccepted?: () => void
}

/**
 * Surfaces app-ownership transfers addressed to the current user, with Accept /
 * Decline actions. Self-contained: fetches its own data and renders nothing when
 * there are no pending offers. Placed on My Apps so a recipient can act even if
 * they have no prior access to the app being handed to them.
 */
export default function IncomingTransfersBanner({ onAccepted }: IncomingTransfersBannerProps) {
  const [transfers, setTransfers] = useState<AppTransfer[]>([])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setTransfers(await listIncomingTransfers())
    } catch {
      // transient / unauthenticated — keep the banner silent
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  async function handleAccept(transfer: AppTransfer) {
    setBusyId(transfer.id)
    setError(null)
    try {
      await acceptTransfer(transfer.id)
      setTransfers((prev) => prev.filter((t) => t.id !== transfer.id))
      onAccepted?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept transfer')
    } finally {
      setBusyId(null)
    }
  }

  async function handleDecline(transfer: AppTransfer) {
    setBusyId(transfer.id)
    setError(null)
    try {
      await declineTransfer(transfer.id)
      setTransfers((prev) => prev.filter((t) => t.id !== transfer.id))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to decline transfer')
    } finally {
      setBusyId(null)
    }
  }

  if (transfers.length === 0) return null

  return (
    <div className="mb-6 space-y-2">
      {transfers.map((transfer) => (
        <div
          key={transfer.id}
          className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border border-amber-500/40 bg-amber-500/10"
        >
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <ArrowRightLeft className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="min-w-0 text-sm">
              <p className="font-medium">
                {transfer.from_name || transfer.from_email || 'Someone'} wants to transfer ownership of{' '}
                <strong>{transfer.app_display_name || transfer.app_name}</strong> to you.
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Accepting makes the app yours; the previous owner keeps only the access they chose.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => handleDecline(transfer)}
              disabled={busyId === transfer.id}
              className="px-3 py-1.5 rounded-lg border border-border hover:bg-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busyId === transfer.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Decline
            </button>
            <button
              onClick={() => handleAccept(transfer)}
              disabled={busyId === transfer.id}
              className="px-3 py-1.5 rounded-lg bg-amber-600 text-white hover:bg-amber-600/90 text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              {busyId === transfer.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Accept
            </button>
          </div>
        </div>
      ))}
      {error && <div className="p-2 rounded-lg bg-destructive/10 text-destructive text-xs">{error}</div>}
    </div>
  )
}
